/**
 * Utility functions for authentication and JWT token management
 *
 * Access token is stored in memory (module variable) for security.
 * Refresh token is stored in httpOnly cookie (managed by backend).
 */

// In-memory storage for access token (not persisted, more secure)
let accessToken: string | null = null;

/**
 * Save access token to memory
 */
export const setToken = (token: string): void => {
  accessToken = token;
};

/**
 * Get access token from memory
 */
export const getToken = (): string | null => accessToken;

/**
 * Clear access token from memory
 */
export const clearAuthData = (): void => {
  accessToken = null;
};

/**
 * Check if user is authenticated (has valid token in memory)
 */
export const isAuthenticated = (): boolean => {
  const token = getToken();
  return token ? !isTokenExpired(token) : false;
};

/**
 * Decode JWT token payload (without verification)
 * This is a simple decoder - DO NOT use for security validation
 */
const decodeToken = (token: string): Record<string, unknown> | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) {
      return null;
    }

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * @param token - JWT token to check
 * @param bufferSeconds - Buffer time in seconds to consider token expired (default: 60s)
 *                        This helps refresh token before it actually expires
 */
export const isTokenExpired = (token: string, bufferSeconds = 60): boolean => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true;
  }

  const currentTime = Date.now() / 1000;
  // Consider token expired if it will expire within buffer time
  return (decoded.exp as number) < currentTime + bufferSeconds;
};

/**
 * Get user info from token
 */
export const getUserFromToken = (): Record<string, unknown> | null => {
  const token = getToken();
  return token ? decodeToken(token) : null;
};
